import { Components, Theme, alpha, chipClasses } from "@mui/material";

import type { ChipColor } from "./Chip";

declare module "@mui/material/Chip" {
  interface ChipClasses {
    colorNeutral: string;
  }
}

const createBlockVariant = (
  color: ChipColor,
  getStyles: (theme: Theme) => {
    color: string;
    backgroundColor: string;
    hoverBackgroundColor: string;
  },
) => ({
  props: { variant: "block", color },
  style: ({ theme }: { theme: Theme }) => {
    const { hoverBackgroundColor, ...styles } = getStyles(theme);

    return {
      borderRadius: theme.spacing(4),
      ...styles,
      [`&&.${chipClasses.clickable}:hover`]: {
        backgroundColor: hoverBackgroundColor,
      },
    };
  },
});

const createOutlinedVariant = (
  color: ChipColor,
  getStyles: (theme: Theme) => {
    color: string;
  },
) => ({
  props: { variant: "outlined", color },
  style: ({ theme }: { theme: Theme }) => {
    const styles = getStyles(theme);

    return {
      ...styles,
      borderColor: alpha(styles.color, 0.5),
      [`&& .${chipClasses.deleteIcon}, && .${chipClasses.icon}`]: {
        color: "inherit",
      },
    };
  },
});

const CHIP_THEME: Components<Theme>["MuiChip"] = {
  defaultProps: {
    // Use the Backpack-specific variant by default to match designs.
    variant: "block",
  },
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => ({
      cursor: "inherit",
      fontFamily: theme.typography.fontFamily,
      [`&.${chipClasses.deletable}`]: {
        justifyContent: "space-evenly",
        [`& .${chipClasses.deleteIcon}`]: {
          margin: 0,
        },
      },
      [`&.${chipClasses.sizeSmall}`]: {
        gap: theme.spacing(4),
      },
      [`&.${chipClasses.sizeMedium}`]: {
        gap: theme.spacing(8),
      },
    }),
    sizeMedium: ({ theme }: { theme: Theme }) => ({
      height: theme.spacing(32),
      padding: theme.spacing(8, 12),
    }),
    sizeSmall: ({ theme }: { theme: Theme }) => ({
      height: theme.spacing(21),
      padding: theme.spacing(4, 6),
    }),
    label: {
      lineHeight: 1,
      padding: 0,
      // Let taller letters overflow the lineHeight
      overflow: "visible",
    },
    labelMedium: ({ theme }: { theme: Theme }) => ({
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(16),
    }),
    labelSmall: ({ theme }: { theme: Theme }) => ({
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(14),
    }),
    icon: {
      margin: 0,
      padding: 0,
    },
    iconMedium: ({ theme }: { theme: Theme }) => ({
      height: theme.spacing(16),
      width: theme.spacing(16),
    }),
    iconSmall: ({ theme }: { theme: Theme }) => ({
      height: theme.spacing(12),
      width: theme.spacing(12),
    }),
  },
  variants: [
    createBlockVariant("default", theme => ({
      color: theme.palette.grey[900],
      backgroundColor: theme.palette.grey[300],
      hoverBackgroundColor: theme.palette.grey[200],
    })),
    createBlockVariant("neutral", theme => ({
      color: theme.palette.grey[900],
      backgroundColor: theme.palette.grey[300],
      hoverBackgroundColor: theme.palette.grey[200],
    })),
    createBlockVariant("primary", theme => ({
      color: theme.palette.primary[800],
      backgroundColor: theme.palette.primary[200],
      hoverBackgroundColor: theme.palette.primary[100],
    })),
    createBlockVariant("secondary", theme => ({
      color: theme.palette.secondary[700],
      backgroundColor: theme.palette.secondary[200],
      hoverBackgroundColor: theme.palette.secondary[100],
    })),
    createBlockVariant("error", theme => ({
      color: theme.palette.error[700],
      backgroundColor: theme.palette.error[200],
      hoverBackgroundColor: theme.palette.error[100],
    })),
    createBlockVariant("warning", theme => ({
      color: theme.palette.warning[800],
      backgroundColor: theme.palette.warning[300],
      hoverBackgroundColor: theme.palette.warning[200],
    })),
    createBlockVariant("info", theme => ({
      color: theme.palette.info[700],
      backgroundColor: theme.palette.info[200],
      hoverBackgroundColor: theme.palette.info[100],
    })),
    createBlockVariant("success", theme => ({
      color: theme.palette.success[700],
      backgroundColor: theme.palette.success[200],
      hoverBackgroundColor: theme.palette.success[100],
    })),
    createOutlinedVariant("default", theme => ({
      color: theme.palette.grey[900],
    })),
    createOutlinedVariant("neutral", theme => ({
      color: theme.palette.neutral.main,
    })),
    createOutlinedVariant("primary", theme => ({
      color: theme.palette.primary.main,
    })),
    createOutlinedVariant("secondary", theme => ({
      color: theme.palette.secondary.main,
    })),
    createOutlinedVariant("error", theme => ({
      color: theme.palette.error.main,
    })),
    createOutlinedVariant("warning", theme => ({
      color: theme.palette.warning.main,
    })),
    createOutlinedVariant("info", theme => ({
      color: theme.palette.info.main,
    })),
    createOutlinedVariant("success", theme => ({
      color: theme.palette.success.main,
    })),
  ],
};

export default CHIP_THEME;
